import { useEffect, useMemo, useState } from "react";
import { WebSignInGate } from "./WebSignInGate";
import { DATA_TOKEN_PAGE_PATH } from "../utils/webPages";
import { fetchChannelList, fetchTurbineList } from "../api/channelSelection";
import { getTigerResource, tigerFormat } from "../utils/tigerResource";
import { acrylicStyles, acrylicTokens } from "../theme/acrylic";
import {
  buildDataUrl,
  buildPowerQueryM,
  fromDatetimeLocal,
  queryProblem,
  toDatetimeLocal,
  type DataFormat,
  type DataKind,
  type DataQuery,
} from "../utils/excelDataUrl";
import type { AuthMode } from "../auth/webAuth";
import type { ChannelInfo, TurbineInfo } from "../types/dialog-types";

export { EXCEL_QUERY_PAGE_PATH, isExcelQueryPage } from "../utils/webPages";

/** Most tags listed at once; the search narrows the rest. */
const MAX_LISTED_TAGS = 200;
/** Rows shown by Test. */
const PREVIEW_ROWS = 10;

/**
 * Excel data query builder (web build), at the unlisted `/exceldata`: pick a
 * turbine, its tags and the data, and get the `/api/data/*` address to paste
 * into Excel's Data > Get Data > From Web — or a Power Query (M) query that
 * also types the columns. Test fetches the first rows with this browser's own
 * session, so a query can be checked before Excel asks for the access token.
 */
export function ExcelQueryPage() {
  return <WebSignInGate>{(mode) => <ExcelQueryBuilder mode={mode} />}</WebSignInGate>;
}

/** Midnight `offsetDays` from today, in the browser's clock. */
function midnight(offsetDays: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offsetDays);
  return d;
}

interface Preview {
  columns: string[];
  rows: unknown[][];
  total: number;
}

function ExcelQueryBuilder({ mode }: { mode: AuthMode }) {
  const [turbines, setTurbines] = useState<TurbineInfo[]>([]);
  const [config, setConfig] = useState("");
  const [kind, setKind] = useState<DataKind>("binary");
  const [channels, setChannels] = useState<ChannelInfo[]>([]);
  const [search, setSearch] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [useRange, setUseRange] = useState(true);
  const [start, setStart] = useState(() => toDatetimeLocal(midnight(-1)));
  const [end, setEnd] = useState(() => toDatetimeLocal(midnight(0)));
  const [step, setStep] = useState("");
  const [format, setFormat] = useState<DataFormat>("csv");
  const [copied, setCopied] = useState<"url" | "m" | null>(null);
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [testError, setTestError] = useState("");

  const trend = kind === "trend";

  useEffect(() => {
    fetchTurbineList().then((list) => {
      setTurbines(list);
      setConfig((c) => c || list[0]?.config_name || "");
    });
  }, []);

  useEffect(() => {
    if (!config) return;
    let cancelled = false;
    fetchChannelList(config, "All", trend ? "trend" : "live").then((list) => {
      if (!cancelled) setChannels(list);
    });
    return () => {
      cancelled = true;
    };
  }, [config, trend]);

  // Anything that changes the address invalidates an earlier test and copy.
  const query: DataQuery = {
    kind,
    config,
    tags,
    range: useRange ? { start: fromDatetimeLocal(start), end: fromDatetimeLocal(end) } : null,
    step: Number(step) > 0 ? Number(step) : null,
    format,
  };
  const url = buildDataUrl(window.location.origin, query);
  const m = buildPowerQueryM(url, query);
  const problem = queryProblem(query);
  useEffect(() => {
    setPreview(null);
    setTestError("");
    setCopied(null);
  }, [url]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle
      ? channels.filter(
          (c) => c.tag.toLowerCase().includes(needle) || c.description.toLowerCase().includes(needle),
        )
      : channels;
  }, [channels, search]);

  function chooseConfig(next: string) {
    setConfig(next);
    setTags([]);
  }

  function chooseKind(next: DataKind) {
    // Trend tags are a different store ("TAG(AVG)"), so a switch to or from
    // trend starts the selection over.
    if ((next === "trend") !== trend) setTags([]);
    setKind(next);
  }

  function toggleTag(tag: string) {
    setTags((t) => (t.includes(tag) ? t.filter((x) => x !== tag) : [...t, tag]));
  }

  async function copy(which: "url" | "m", text: string, fieldId: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
    } catch {
      // No clipboard (plain http, or permission refused): select the field so
      // Ctrl+C works instead.
      (document.getElementById(fieldId) as HTMLTextAreaElement | null)?.select();
    }
  }

  async function onTest() {
    setTesting(true);
    setPreview(null);
    setTestError("");
    try {
      // X-Requested-With: an expired session comes back as a plain 401, not a
      // Basic challenge that would pop the browser's own sign-in box.
      const res = await fetch(buildDataUrl(window.location.origin, { ...query, format: "json" }), {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (!res.ok) {
        setTestError(`${res.status}: ${(await res.text()).trim()}`);
      } else {
        const body = (await res.json()) as { columns: string[]; rows: unknown[][] };
        setPreview({ columns: body.columns, rows: body.rows.slice(0, PREVIEW_ROWS), total: body.rows.length });
      }
    } catch (e) {
      setTestError(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  }

  const r = getTigerResource;
  const title = r("TIGER_XL_TITLE");

  return (
    <div style={styles.page}>
      <main style={styles.card} aria-label={title}>
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
        </div>

        <div style={styles.body}>
          <span style={styles.message}>
            {r("TIGER_XL_INTRO")}{" "}
            {mode === "none" ? (
              r("TIGER_XL_NO_TOKEN_NEEDED")
            ) : (
              <a href={DATA_TOKEN_PAGE_PATH} style={styles.inlineLink}>
                {r("TIGER_XL_TOKEN_LINK")}
              </a>
            )}
          </span>

          <div style={styles.columns}>
            <div style={styles.column}>
              <label style={styles.field}>
                <span style={styles.fieldLabel}>{r("TIGER_XL_CONFIG")}</span>
                <select
                  aria-label={r("TIGER_XL_CONFIG")}
                  value={config}
                  onChange={(e) => chooseConfig(e.target.value)}
                  style={styles.input}
                >
                  {turbines.map((t) => (
                    <option key={t.config_name} value={t.config_name}>
                      {t.turbine_name} ({t.config_name})
                    </option>
                  ))}
                </select>
              </label>

              <fieldset style={styles.fieldset}>
                <legend style={styles.fieldLabel}>{r("TIGER_XL_KIND")}</legend>
                {(["binary", "trend", "current"] as const).map((k) => (
                  <label key={k} style={styles.radio}>
                    <input type="radio" name="kind" checked={kind === k} onChange={() => chooseKind(k)} />{" "}
                    {r(`TIGER_XL_KIND_${k.toUpperCase()}`)}
                  </label>
                ))}
              </fieldset>

              {kind !== "current" && (
                <>
                  <fieldset style={styles.fieldset}>
                    <legend style={styles.fieldLabel}>{r("TIGER_XL_RANGE")}</legend>
                    <label style={styles.radio}>
                      <input type="radio" name="range" checked={useRange} onChange={() => setUseRange(true)} />{" "}
                      {r("TIGER_XL_RANGE_FIXED")}
                    </label>
                    <label style={styles.radio}>
                      <input type="radio" name="range" checked={!useRange} onChange={() => setUseRange(false)} />{" "}
                      {r("TIGER_XL_RANGE_LIVE")}
                    </label>
                  </fieldset>
                  {useRange && (
                    <div style={styles.row}>
                      <label style={styles.field}>
                        <span style={styles.fieldLabel}>{r("TIGER_XL_FROM")}</span>
                        <input
                          aria-label={r("TIGER_XL_FROM")}
                          type="datetime-local"
                          value={start}
                          onChange={(e) => setStart(e.target.value)}
                          style={styles.input}
                        />
                      </label>
                      <label style={styles.field}>
                        <span style={styles.fieldLabel}>{r("TIGER_XL_TO")}</span>
                        <input
                          aria-label={r("TIGER_XL_TO")}
                          type="datetime-local"
                          value={end}
                          onChange={(e) => setEnd(e.target.value)}
                          style={styles.input}
                        />
                      </label>
                    </div>
                  )}
                  <label style={styles.field}>
                    <span style={styles.fieldLabel}>{r("TIGER_XL_STEP")}</span>
                    <input
                      aria-label={r("TIGER_XL_STEP")}
                      type="number"
                      min={0}
                      value={step}
                      onChange={(e) => setStep(e.target.value)}
                      style={styles.input}
                    />
                  </label>
                </>
              )}

              <fieldset style={styles.fieldset}>
                <legend style={styles.fieldLabel}>{r("TIGER_XL_FORMAT")}</legend>
                {(["csv", "json"] as const).map((f) => (
                  <label key={f} style={styles.radio}>
                    <input type="radio" name="format" checked={format === f} onChange={() => setFormat(f)} />{" "}
                    {f.toUpperCase()}
                  </label>
                ))}
              </fieldset>
            </div>

            <div style={styles.column}>
              <label style={styles.field}>
                <span style={styles.fieldLabel}>{r("TIGER_XL_TAGS")}</span>
                <input
                  aria-label={r("TIGER_XL_SEARCH")}
                  placeholder={r("TIGER_XL_SEARCH")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={styles.input}
                />
              </label>
              <div style={styles.tagList} role="list">
                {filtered.slice(0, MAX_LISTED_TAGS).map((c) => (
                  <label key={c.tag} style={styles.tagRow} role="listitem">
                    <input type="checkbox" checked={tags.includes(c.tag)} onChange={() => toggleTag(c.tag)} />
                    <span style={styles.tagName}>{c.tag}</span>
                    <span style={styles.tagDescr}>{c.description}</span>
                  </label>
                ))}
                {filtered.length > MAX_LISTED_TAGS && (
                  <span style={styles.note}>
                    {tigerFormat(r("TIGER_XL_MORE"), String(filtered.length - MAX_LISTED_TAGS))}
                  </span>
                )}
              </div>
              <span style={styles.fieldLabel}>{r("TIGER_XL_SELECTED")}</span>
              <div style={styles.chips}>
                {tags.length === 0 && <span style={styles.note}>{r("TIGER_XL_NONE_SELECTED")}</span>}
                {tags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    style={styles.chip}
                    onClick={() => toggleTag(t)}
                    aria-label={`${r("TIGER_XL_REMOVE")} ${t}`}
                  >
                    {t} &#x2715;
                  </button>
                ))}
              </div>
            </div>
          </div>

          {problem ? (
            <div style={styles.error}>{r(problem)}</div>
          ) : (
            <>
              <label style={styles.field}>
                <span style={styles.fieldLabel}>{r("TIGER_XL_ADDRESS")}</span>
                <div style={styles.copyRow}>
                  <textarea
                    id="excel-query-url"
                    aria-label={r("TIGER_XL_ADDRESS")}
                    readOnly
                    rows={2}
                    value={url}
                    onFocus={(e) => e.target.select()}
                    style={{ ...styles.input, ...styles.code, flex: 1 }}
                  />
                  <div style={styles.buttons}>
                    <button type="button" className="btn-acrylic-primary" onClick={() => copy("url", url, "excel-query-url")}>
                      {r(copied === "url" ? "TIGER_DATA_TOKEN_COPIED" : "TIGER_DATA_TOKEN_COPY")}
                    </button>
                    <button type="button" className="btn-acrylic-secondary" disabled={testing} onClick={onTest}>
                      {r(testing ? "TIGER_XL_TESTING" : "TIGER_XL_TEST")}
                    </button>
                  </div>
                </div>
              </label>

              {testError && <div style={styles.error}>{testError}</div>}
              {preview && (
                <div style={styles.preview}>
                  <span style={styles.note}>{tigerFormat(r("TIGER_XL_TEST_ROWS"), String(preview.total))}</span>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {preview.columns.map((c) => (
                          <th key={c} style={styles.cell}>
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, i) => (
                        <tr key={i}>
                          {row.map((v, j) => (
                            <td key={j} style={styles.cell}>
                              {v === null ? "" : String(v)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <label style={styles.field}>
                <span style={styles.fieldLabel}>{r("TIGER_XL_PQ")}</span>
                <span style={styles.note}>{r("TIGER_XL_PQ_HINT")}</span>
                <div style={styles.copyRow}>
                  <textarea
                    id="excel-query-m"
                    aria-label={r("TIGER_XL_PQ")}
                    readOnly
                    rows={7}
                    value={m}
                    onFocus={(e) => e.target.select()}
                    style={{ ...styles.input, ...styles.code, flex: 1 }}
                  />
                  <div style={styles.buttons}>
                    <button type="button" className="btn-acrylic-secondary" onClick={() => copy("m", m, "excel-query-m")}>
                      {r(copied === "m" ? "TIGER_DATA_TOKEN_COPIED" : "TIGER_DATA_TOKEN_COPY")}
                    </button>
                  </div>
                </div>
              </label>
            </>
          )}
        </div>

        <div style={styles.footer}>
          <a href="/" className="btn-acrylic-secondary" style={styles.link}>
            {r("TIGER_DATA_TOKEN_BACK")}
          </a>
        </div>
      </main>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: 16,
    boxSizing: "border-box" as const,
  },
  card: {
    background: acrylicTokens.surface,
    backdropFilter: acrylicTokens.surfaceBlur,
    WebkitBackdropFilter: acrylicTokens.surfaceBlur,
    border: acrylicTokens.surfaceBorder,
    borderRadius: acrylicTokens.surfaceRadius,
    width: 980,
    maxWidth: "100%",
    boxShadow: acrylicTokens.surfaceShadow,
    overflow: "hidden" as const,
  },
  body: { padding: "18px 24px", display: "flex", flexDirection: "column" as const, gap: 12 },
  columns: { display: "flex", flexWrap: "wrap" as const, gap: 24 },
  column: { flex: "1 1 400px", minWidth: 0, display: "flex", flexDirection: "column" as const, gap: 10 },
  row: { display: "flex", gap: 12, flexWrap: "wrap" as const },
  message: { fontSize: 14, color: "#4b5260" },
  note: { fontSize: 12, color: "#4b5260" },
  field: { display: "flex", flexDirection: "column" as const, gap: 4 },
  fieldLabel: { fontSize: 12, color: "#4b5260" },
  fieldset: { border: "none", padding: 0, margin: 0, display: "flex", gap: 14, flexWrap: "wrap" as const },
  radio: { fontSize: 13, display: "flex", alignItems: "center", gap: 4 },
  input: {
    padding: "7px 9px",
    borderRadius: 6,
    border: "1px solid rgba(0,0,0,0.15)",
    fontSize: 13,
  },
  code: { fontFamily: "Consolas, monospace", fontSize: 12, resize: "vertical" as const },
  tagList: {
    height: 260,
    overflowY: "auto" as const,
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 6,
    padding: "4px 6px",
    background: "rgba(255,255,255,0.6)",
  },
  tagRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "2px 0" },
  tagName: { fontFamily: "Consolas, monospace", minWidth: 120 },
  tagDescr: { color: "#4b5260", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const },
  chips: { display: "flex", flexWrap: "wrap" as const, gap: 6, minHeight: 24 },
  chip: {
    fontSize: 12,
    fontFamily: "Consolas, monospace",
    border: "1px solid rgba(59,91,219,0.4)",
    background: "rgba(59,91,219,0.08)",
    borderRadius: 12,
    padding: "2px 8px",
    cursor: "pointer",
  },
  copyRow: { display: "flex", gap: 8, alignItems: "flex-start" },
  buttons: { display: "flex", flexDirection: "column" as const, gap: 6 },
  preview: { display: "flex", flexDirection: "column" as const, gap: 4, overflowX: "auto" as const },
  table: { borderCollapse: "collapse" as const, fontSize: 12 },
  cell: { border: "1px solid rgba(0,0,0,0.12)", padding: "2px 6px", textAlign: "left" as const, whiteSpace: "nowrap" as const },
  link: { textDecoration: "none" },
  inlineLink: { color: "#3b5bdb" },
  error: { fontSize: 12, color: "#b91c1c" },
} as const;
